import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { useSiteConfig, DEFAULT_CONFIG } from '../context/SiteConfigContext';
import api from '../api/axios';
import { refreshChurchSettings, DEFAULT_SETTINGS } from '../hooks/useChurchSettings';
import { useAuth } from '../context/AuthContext';
import { useChurch } from '../context/ChurchContext';
import AfficheCard from '../components/common/AfficheCard';
import usePermissions from '../hooks/usePermissions';

/* ─────────────────────── helpers ─────────────────────── */
const PRESET_COLORS = [
  // Verts & naturels
  '#4A7C59','#2D4A3E','#5B8C5A','#3A6347',
  // Dorés & bruns
  '#8B7355','#6B5744','#C0883A','#A06C28',
  // Bleus classiques
  '#1e40af','#0f766e','#0369a1','#0891b2',
  // Autres
  '#7c3aed','#b45309','#b91c1c','#1e293b',
];

const FONT_OPTIONS = [
  { label: 'System UI (défaut)', value: 'system-ui, -apple-system, sans-serif' },
  { label: 'Inter',              value: 'Inter, system-ui, sans-serif' },
  { label: 'Georgia (serif)',    value: 'Georgia, Cambria, serif' },
  { label: 'Monospace',          value: 'ui-monospace, monospace' },
];

const SHADOW_OPTIONS = [
  { label: 'Aucune',  value: 'none' },
  { label: 'Légère',  value: '0 1px 3px rgba(0,0,0,0.07)' },
  { label: 'Moyenne', value: '0 4px 12px rgba(0,0,0,0.1)' },
  { label: 'Forte',   value: '0 8px 24px rgba(0,0,0,0.14)' },
];

function ColorPicker({ label, value, onChange }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={s.label}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {PRESET_COLORS.map(c => (
            <button key={c} onClick={() => onChange(c)}
              style={{ width: '28px', height: '28px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: c, outline: value === c ? `3px solid ${c}` : '3px solid transparent', outlineOffset: '2px', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}
            />
          ))}
        </div>
        <input type="color" value={value} onChange={e => onChange(e.target.value)}
          style={{ width: '36px', height: '36px', borderRadius: '6px', border: '1px solid #d1d5db', cursor: 'pointer', padding: '1px' }} />
        <input style={{ ...s.input, maxWidth: '100px', fontFamily: 'monospace', fontSize: '0.85rem' }}
          value={value} onChange={e => onChange(e.target.value)} maxLength={7} placeholder="#000000" />
      </div>
    </div>
  );
}

function RangeInput({ label, value, min, max, onChange, unit = 'px' }) {
  return (
    <div style={{ marginBottom: '12px' }}>
      <label style={s.label}>{label} <span style={{ color: '#1e40af', fontWeight: 700 }}>{value}{unit}</span></label>
      <input type="range" min={min} max={max} value={value}
        onChange={e => onChange(e.target.value)}
        style={{ width: '100%', accentColor: 'var(--primary)', cursor: 'pointer' }} />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
        <span>{min}{unit}</span><span>{max}{unit}</span>
      </div>
    </div>
  );
}

const s = {
  label:   { display: 'block', fontSize: '0.83rem', fontWeight: 600, color: '#374151', marginBottom: '5px' },
  input:   { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '0.92rem', color: '#1e293b', boxSizing: 'border-box' },
  section: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '16px', overflow: 'hidden' },
  sHead:   { padding: '14px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#fafafa' },
  sBody:   { padding: '18px 20px' },
  sTitle:  { fontWeight: 700, fontSize: '0.97rem', color: '#1e293b' },
  sDesc:   { fontSize: '0.78rem', color: '#64748b', marginTop: '1px' },
  twoCol:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  row:     { display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '12px' },
};

const STATUS_LABELS_CARD = {
  nouveau: 'Nouveau',
  membre: 'Membre régulier',
  serviteur: 'Serviteur',
  invite: 'Invité',
};

// Preview d'une carte membre — même style que MemberCard dans FidelePortal
function CardPreview({ churchName, logoPreview, colors, status = 'membre', watermarkText = '', watermarkOpacity = 0.08, cardBottomText = '', cardPhotoCaption = '' }) {
  const c = colors[status] || { from: '#1e40af', to: '#1e3a8a' };
  const color = c.from;
  return (
    <div style={{
      borderRadius: '14px', overflow: 'hidden',
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      background: '#ffffff',
      width: '100%', maxWidth: '320px',
      aspectRatio: '1.586 / 1',
      display: 'flex', flexDirection: 'column',
      position: 'relative',
    }}>
      {/* Fond décoratif + filigrane */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', top: '-30px', right: '-40px', width: '140px', height: '140px', borderRadius: '50%', background: `${color}12` }}/>
        <div style={{ position: 'absolute', bottom: '-15px', left: '-20px', width: '90px', height: '90px', borderRadius: '50%', background: `${color}08` }}/>
        {watermarkText && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: watermarkOpacity, transform: 'rotate(-30deg)', userSelect: 'none' }}>
            <span style={{ fontSize: '2rem', fontWeight: 900, color, letterSpacing: '0.05em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{watermarkText}</span>
          </div>
        )}
        {!watermarkText && logoPreview && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: watermarkOpacity }}>
            <img src={logoPreview} alt="" style={{ width: '55%', objectFit: 'contain' }}/>
          </div>
        )}
      </div>

      {/* En-tête */}
      <div style={{ background: `linear-gradient(120deg, ${color} 0%, ${color}d0 100%)`, padding: '7px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.22)', borderRadius: '5px', padding: '4px', display: 'flex' }}>
            {logoPreview
              ? <img src={logoPreview} alt="logo" style={{ width: '14px', height: '14px', objectFit: 'contain' }} />
              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            }
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 900, color: 'white', lineHeight: 1.1 }}>{churchName || 'RCC'}</div>
            <div style={{ fontSize: '0.38rem', color: 'rgba(255,255,255,0.65)', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Carte de membre</div>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.36rem', color: 'rgba(255,255,255,0.6)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>N° Membre</div>
          <div style={{ fontFamily: 'monospace', fontWeight: 900, fontSize: '0.58rem', color: 'white', letterSpacing: '0.06em' }}>EGL-7368</div>
        </div>
      </div>

      {/* Corps */}
      <div style={{ flex: 1, display: 'flex', gap: '10px', padding: '8px 12px 6px', overflow: 'hidden' }}>
        {/* Photo placeholder */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ width: '60px', height: '74px', borderRadius: '6px', backgroundColor: `${color}18`, boxShadow: `0 3px 12px ${color}35`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
              <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: `${color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 900, color }}>AF</span>
              </div>
            </div>
          </div>
          {cardPhotoCaption && (
            <div style={{ marginTop: '3px', fontSize: '0.34rem', color: `${color}90`, textAlign: 'center', fontStyle: 'italic', lineHeight: 1.3, maxWidth: '60px' }}>
              {cardPhotoCaption}
            </div>
          )}
        </div>

        {/* Infos */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.1 }}>Alice <span style={{ color }}> FOFANA</span></div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '3px', background: `${color}14`, borderRadius: '20px', padding: '1px 6px' }}>
              <div style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: color }}/>
              <span style={{ fontSize: '0.5rem', fontWeight: 700, color }}>{STATUS_LABELS_CARD[status]}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px 6px' }}>
            {[['Genre','Homme'],['Né(e)','2001'],['Tél.','0759177681'],['Ville','Abidjan']].map(([l,v]) => (
              <div key={l}>
                <div style={{ fontSize: '0.36rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{l}</div>
                <div style={{ fontSize: '0.55rem', fontWeight: 600, color: '#1e293b' }}>{v}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '0.36rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Présences</div>
              <div style={{ fontSize: '1rem', fontWeight: 900, color, lineHeight: 1 }}>24</div>
            </div>
            <div style={{ borderRadius: '5px', padding: '2px', background: 'white', boxShadow: `0 2px 8px ${color}20`, border: `1px solid ${color}18` }}>
              <QRCodeSVG value="example-uuid" size={36} fgColor={color} level="M"/>
            </div>
          </div>
        </div>
      </div>

      {/* Pied */}
      <div style={{ background: `linear-gradient(120deg, ${color}ee 0%, ${color}aa 100%)`, padding: '4px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        <span style={{ fontSize: '0.38rem', color: 'rgba(255,255,255,0.78)', letterSpacing: '0.05em' }}>{cardBottomText || 'Scannez le QR pour marquer votre présence'}</span>
        <span style={{ fontSize: '0.36rem', color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace' }}>EGL-7368</span>
      </div>
    </div>
  );
}


export default function SiteSettings() {
  const { config, updateConfig, resetConfig } = useSiteConfig();
  const { user } = useAuth();
  const { activeChurchId, canSwitchChurch } = useChurch();
  const { isAdmin: isRealAdmin, hasGroupAccess, inGroupOnlyMode } = usePermissions();
  const canEditNews = isRealAdmin ? !inGroupOnlyMode || hasGroupAccess('notifications') : hasGroupAccess('notifications');
  const isPlatformAdmin = user?.role === 'superadmin_platform';
  const fileRef = useRef(null);
  const logoFileRef = useRef(null);

  const [form, setForm] = useState({ ...config });
  const upd = (key, val) => setForm(p => ({ ...p, [key]: val }));

  // ── Church card settings ──
  const [church, setChurch] = useState(DEFAULT_SETTINGS);
  const [churchSaving, setChurchSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [removeLogo, setRemoveLogo] = useState(false);
  const [previewStatus, setPreviewStatus] = useState('membre');
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkOpacity, setWatermarkOpacity] = useState(0.08);
  const [requirePassword, setRequirePassword] = useState(false);
  const [useFamilies, setUseFamilies] = useState(false);
  const [cardBottomText, setCardBottomText] = useState('Scannez le QR pour marquer votre présence');
  const [cardPhotoCaption, setCardPhotoCaption] = useState('');
  const [news, setNews] = useState([]);

  // ── Email config ──
  const EMPTY_EMAIL_CFG = { enabled: false, smtp_host: 'smtp.gmail.com', smtp_port: 587, smtp_use_tls: true, smtp_use_ssl: false, smtp_username: '', smtp_password: '', from_email: '', from_name: '', admin_email: '' };
  const [emailCfg, setEmailCfg] = useState(EMPTY_EMAIL_CFG);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTesting, setEmailTesting] = useState(false);
  const [showSmtpPwd, setShowSmtpPwd] = useState(false);

  useEffect(() => {
    if (!isPlatformAdmin) return;
    api.get('/notifications/email-config/').then(r => {
      setEmailCfg(prev => ({ ...prev, ...r.data, smtp_password: '' }));
    }).catch(() => {});
  }, [isPlatformAdmin]);

  const handleSaveEmail = async () => {
    setEmailSaving(true);
    try {
      const payload = { ...emailCfg };
      delete payload.has_password;
      if (!payload.smtp_password) delete payload.smtp_password; // don't overwrite if blank
      const res = await api.put('/notifications/email-config/', payload);
      toast.success('Configuration email enregistrée !');
      setEmailCfg(prev => ({ ...prev, ...res.data, smtp_password: '' }));
    } catch {
      toast.error('Erreur lors de la sauvegarde.');
    } finally { setEmailSaving(false); }
  };

  const handleTestEmail = async () => {
    // Si un mot de passe est saisi mais pas encore enregistré, on sauvegarde d'abord
    if (emailCfg.smtp_password) {
      try {
        const payload = { ...emailCfg };
        delete payload.has_password;
        const res = await api.put('/notifications/email-config/', payload);
        setEmailCfg(prev => ({ ...prev, ...res.data, smtp_password: '' }));
      } catch {
        toast.error('Erreur lors de la sauvegarde de la configuration avant test.');
        return;
      }
    }
    setEmailTesting(true);
    try {
      const res = await api.post('/notifications/email-config/test/');
      toast.success(res.data.message || 'Email de test envoyé !');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Échec de l\'envoi.');
    } finally { setEmailTesting(false); }
  };

  const applyChurchData = (r) => {
    setChurch(r);
    setLogoPreview(r.logo || null);
    setLogoFile(null);
    setRemoveLogo(false);
    setWatermarkText(r.watermark_text || '');
    setWatermarkOpacity(r.watermark_opacity ?? 0.08);
    setRequirePassword(r.require_password ?? false);
    setUseFamilies(r.use_families ?? false);
    setCardBottomText(r.card_bottom_text ?? 'Scannez le QR pour marquer votre présence');
    setCardPhotoCaption(r.card_photo_caption || '');
    setNews(r.landing_news || []);
    // Synchroniser la section "Identité" avec le backend (source de vérité)
    setForm(prev => ({
      ...prev,
      siteName: r.church_name || prev.siteName,
      logoUrl:  r.logo || prev.logoUrl,
    }));
  };

  useEffect(() => {
    // Vider immédiatement pour ne jamais montrer les données de l'ancienne église
    setChurch({ ...DEFAULT_SETTINGS });
    setLogoPreview(null);
    setForm(prev => ({ ...prev, siteName: '', logoUrl: '' }));
    const cid = activeChurchId;
    api.get('/notifications/church-settings/', { params: cid ? { church_id: cid } : {} })
      .then(r => applyChurchData(r.data))
      .catch(() => {});
  }, [activeChurchId, isPlatformAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const updChurch = (key, val) => setChurch(p => ({ ...p, [key]: val }));
  const updColor = (status, side, val) => setChurch(p => ({
    ...p,
    colors: { ...p.colors, [status]: { ...p.colors[status], [side]: val } },
  }));

  const handleLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image trop lourde (max 2 Mo).'); return; }
    setLogoFile(file);
    setRemoveLogo(false);
    const reader = new FileReader();
    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setRemoveLogo(true);
  };

  const handleSaveChurch = async () => {
    setChurchSaving(true);
    try {
      const fd = new FormData();
      if (canSwitchChurch && activeChurchId) fd.append('church_id', activeChurchId);
      fd.append('church_name', church.church_name);
      const colors = church.colors || {};
      fd.append('color_nouveau',    colors.nouveau?.from   || '#000000');
      fd.append('color_nouveau_to', colors.nouveau?.to     || '#000000');
      fd.append('color_membre',     colors.membre?.from    || '#000000');
      fd.append('color_membre_to',  colors.membre?.to      || '#000000');
      fd.append('color_serviteur',  colors.serviteur?.from || '#000000');
      fd.append('color_serviteur_to', colors.serviteur?.to || '#000000');
      fd.append('color_invite',     colors.invite?.from    || '#000000');
      fd.append('color_invite_to',  colors.invite?.to      || '#000000');
      if (logoFile) fd.append('logo', logoFile);
      if (removeLogo) fd.append('remove_logo', 'true');
      fd.append('watermark_text', watermarkText);
      fd.append('watermark_opacity', watermarkOpacity);
      fd.append('require_password', requirePassword);
      fd.append('use_families', useFamilies);
      fd.append('card_bottom_text', cardBottomText);
      fd.append('card_photo_caption', cardPhotoCaption);

      const putParams = activeChurchId ? { church_id: activeChurchId } : {};
      const res = await api.put('/notifications/church-settings/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: putParams,
      });
      setChurch(res.data);
      setLogoPreview(res.data.logo || null);
      setWatermarkText(res.data.watermark_text || '');
      setWatermarkOpacity(res.data.watermark_opacity ?? 0.08);
      setRequirePassword(res.data.require_password ?? false);
      setUseFamilies(res.data.use_families ?? false);
      setCardBottomText(res.data.card_bottom_text ?? 'Scannez le QR pour marquer votre présence');
      setCardPhotoCaption(res.data.card_photo_caption || '');
      setLogoFile(null);
      setRemoveLogo(false);
      await refreshChurchSettings(activeChurchId);
      // Synchroniser la navbar (siteName, logoUrl) avec le nom/logo de l'église
      updateConfig({
        siteName: res.data.church_name || church.church_name,
        logoUrl: res.data.logo || '',
      });
      toast.success('Paramètres des cartes enregistrés !');
    } catch {
      toast.error('Erreur lors de la sauvegarde.');
    } finally {
      setChurchSaving(false);
    }
  };

  const [newsSaving, setNewsSaving] = useState(false);
  const handleSaveNews = async () => {
    setNewsSaving(true);
    try {
      const params = canSwitchChurch && activeChurchId ? { church_id: activeChurchId } : {};
      const res = await api.patch('/notifications/church-settings/landing-news/', { landing_news: news }, { params });
      setNews(res.data.landing_news || []);
      toast.success('Affiches enregistrées !');
    } catch (e) {
      if (e?.response?.status !== 403) toast.error('Erreur lors de la sauvegarde des affiches.');
    } finally {
      setNewsSaving(false);
    }
  };

  // Sauvegarde spécifique de la page d'accueil — toujours en global (sans church_id)

  // Preview apply (live)
  const applyPreview = () => updateConfig(form);

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error('Image trop lourde (max 2 Mo).'); return; }
    const reader = new FileReader();
    reader.onload = ev => { upd('logoUrl', ev.target.result); };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    updateConfig(form);
    try {
      const fd = new FormData();
      if (canSwitchChurch && activeChurchId) fd.append('church_id', activeChurchId);
      if (form.siteName) fd.append('church_name', form.siteName);
      fd.append('theme_config', JSON.stringify(form));
      const putParams = activeChurchId ? { church_id: activeChurchId } : {};
      await api.put('/notifications/church-settings/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: putParams,
      });
      toast.success('Thème enregistré et appliqué !');
    } catch {
      toast.success('Thème appliqué localement.');
    }
  };

  const handleReset = async () => {
    const d = { ...DEFAULT_CONFIG };
    setForm(d);
    resetConfig();
    try {
      const fd = new FormData();
      if (canSwitchChurch && activeChurchId) fd.append('church_id', activeChurchId);
      fd.append('theme_config', '{}');
      const putParams = activeChurchId ? { church_id: activeChurchId } : {};
      await api.put('/notifications/church-settings/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: putParams,
      });
    } catch { /* silencieux */ }
    toast.success('Thème réinitialisé.');
  };

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '22px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', margin: 0 }}>Paramètres du site</h1>
          <p style={{ color: '#64748b', fontSize: '0.88rem', marginTop: '4px', marginBottom: 0 }}>
            Personnalisez l'apparence complète de l'interface — comme un CMS.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{ backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}
            onClick={applyPreview}>
            Aperçu en direct
          </button>
          <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', padding: '9px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}
            onClick={handleReset}>
            Réinitialiser
          </button>
          <button style={{ backgroundColor: form.primaryColor, color: 'white', border: 'none', borderRadius: '8px', padding: '9px 20px', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem' }}
            onClick={handleSave}>
            Enregistrer
          </button>
        </div>
      </div>

      {/* ── Bandeau mode global ── */}
      {isPlatformAdmin && !activeChurchId && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', border: '1px solid #c7d2fe', borderRadius: '12px', padding: '14px 18px', marginBottom: '20px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#3730a3' }}>Paramètres communs à toutes les églises</div>
            <div style={{ fontSize: '0.78rem', color: '#6366f1', marginTop: '2px' }}>
              Les modifications ici s'appliquent à toutes les églises qui n'ont pas de config personnalisée.
            </div>
          </div>
        </div>
      )}

      {/* ── IDENTITÉ — masquée en mode global (propre à chaque église) ── */}
      {(activeChurchId || !canSwitchChurch) && <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          <div><div style={s.sTitle}>Identité</div><div style={s.sDesc}>Nom et logo affichés dans la navbar</div></div>
        </div>
        <div style={s.sBody}>
          <div style={s.twoCol}>
            <div>
              <label style={s.label}>Nom de l'église</label>
              <input style={s.input} value={form.siteName} onChange={e => upd('siteName', e.target.value)} placeholder="RCC" maxLength={60} />
            </div>
            <div>
              <label style={s.label}>Logo</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '8px', border: '2px dashed #d1d5db', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {form.logoUrl
                    ? <img src={form.logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  }
                </div>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoUpload} />
                <button style={{ backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
                  onClick={() => fileRef.current?.click()}>
                  Choisir
                </button>
                {form.logoUrl && (
                  <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                    onClick={() => upd('logoUrl', '')}>
                    Suppr.
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>}

      {/* ── COULEURS PRINCIPALES ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><circle cx="13.5" cy="6.5" r=".5" fill="#7c3aed"/><circle cx="17.5" cy="10.5" r=".5" fill="#7c3aed"/><circle cx="8.5" cy="7.5" r=".5" fill="#7c3aed"/><circle cx="6.5" cy="12.5" r=".5" fill="#7c3aed"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"/></svg>
          <div><div style={s.sTitle}>Couleurs principales</div><div style={s.sDesc}>Navbar, boutons, liens actifs, accents</div></div>
        </div>
        <div style={s.sBody}>
          <ColorPicker label="Couleur principale (navbar, boutons)" value={form.primaryColor} onChange={v => { upd('primaryColor', v); upd('btnPrimaryBg', v); upd('sidebarActive', v); upd('accentColor', v); }} />
          <ColorPicker label="Fond de la sidebar" value={form.sidebarBg} onChange={v => upd('sidebarBg', v)} />
          <ColorPicker label="Texte inactif sidebar" value={form.sidebarText} onChange={v => upd('sidebarText', v)} />
        </div>
      </div>

      {/* ── FOND & SURFACES ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0891b2" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
          <div><div style={s.sTitle}>Fond & Surfaces</div><div style={s.sDesc}>Arrière-plan des pages, cartes et bordures</div></div>
        </div>
        <div style={s.sBody}>
          <div style={s.twoCol}>
            <ColorPicker label="Fond des pages" value={form.pageBg} onChange={v => upd('pageBg', v)} />
            <ColorPicker label="Fond des cartes" value={form.cardBg} onChange={v => upd('cardBg', v)} />
          </div>
          <ColorPicker label="Couleur des bordures" value={form.borderColor} onChange={v => upd('borderColor', v)} />
        </div>
      </div>

      {/* ── TYPOGRAPHIE ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#92400e" strokeWidth="2"><polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/></svg>
          <div><div style={s.sTitle}>Typographie</div><div style={s.sDesc}>Police et taille de base</div></div>
        </div>
        <div style={s.sBody}>
          <div style={s.twoCol}>
            <div style={{ marginBottom: '12px' }}>
              <label style={s.label}>Police de caractères</label>
              <select style={{ ...s.input, backgroundColor: 'white', cursor: 'pointer' }}
                value={form.fontFamily} onChange={e => upd('fontFamily', e.target.value)}>
                {FONT_OPTIONS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </div>
            <RangeInput label="Taille de base" value={form.fontSize} min="13" max="18" onChange={v => upd('fontSize', v)} />
          </div>
          <div style={s.twoCol}>
            <ColorPicker label="Texte principal" value={form.textPrimary} onChange={v => upd('textPrimary', v)} />
            <ColorPicker label="Texte secondaire" value={form.textSecondary} onChange={v => upd('textSecondary', v)} />
          </div>
        </div>
      </div>

      {/* ── BOUTONS ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#15803d" strokeWidth="2"><rect x="2" y="7" width="20" height="10" rx="3"/></svg>
          <div><div style={s.sTitle}>Boutons</div><div style={s.sDesc}>Style et couleurs des boutons</div></div>
        </div>
        <div style={s.sBody}>
          <RangeInput label="Arrondi des boutons" value={form.btnRadius} min="0" max="24" onChange={v => upd('btnRadius', v)} />
          <div style={s.twoCol}>
            <div>
              <ColorPicker label="Bouton primaire — fond" value={form.btnPrimaryBg} onChange={v => upd('btnPrimaryBg', v)} />
              <ColorPicker label="Bouton primaire — texte" value={form.btnPrimaryText} onChange={v => upd('btnPrimaryText', v)} />
            </div>
            <div>
              <ColorPicker label="Bouton secondaire — fond" value={form.btnSecondaryBg} onChange={v => upd('btnSecondaryBg', v)} />
              <ColorPicker label="Bouton secondaire — texte" value={form.btnSecondaryText} onChange={v => upd('btnSecondaryText', v)} />
            </div>
          </div>
          {/* Preview boutons */}
          <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '14px 16px', border: '1px solid #e2e8f0', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.78rem', color: '#64748b', marginRight: '4px' }}>Aperçu :</span>
            <button style={{ backgroundColor: form.btnPrimaryBg, color: form.btnPrimaryText, border: 'none', borderRadius: form.btnRadius + 'px', padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Primaire</button>
            <button style={{ backgroundColor: form.btnSecondaryBg, color: form.btnSecondaryText, border: `1px solid ${form.borderColor}`, borderRadius: form.btnRadius + 'px', padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Secondaire</button>
            <button style={{ backgroundColor: form.btnDangerBg, color: form.btnDangerText, border: '1px solid #fecaca', borderRadius: form.btnRadius + 'px', padding: '8px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem' }}>Danger</button>
          </div>
        </div>
      </div>

      {/* ── CARTES ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="3"/><line x1="2" y1="9" x2="22" y2="9"/></svg>
          <div><div style={s.sTitle}>Cartes & Sections</div><div style={s.sDesc}>Arrondi et ombre des panneaux</div></div>
        </div>
        <div style={s.sBody}>
          <div style={s.twoCol}>
            <RangeInput label="Arrondi des cartes" value={form.cardRadius} min="0" max="24" onChange={v => upd('cardRadius', v)} />
            <div style={{ marginBottom: '12px' }}>
              <label style={s.label}>Ombre des cartes</label>
              <select style={{ ...s.input, backgroundColor: 'white', cursor: 'pointer' }}
                value={form.cardShadow} onChange={e => upd('cardShadow', e.target.value)}>
                {SHADOW_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          {/* Preview carte */}
          <div style={{ backgroundColor: form.pageBg, borderRadius: '8px', padding: '14px', border: `1px solid ${form.borderColor}` }}>
            <div style={{ backgroundColor: form.cardBg, borderRadius: form.cardRadius + 'px', border: `1px solid ${form.borderColor}`, boxShadow: form.cardShadow, padding: '16px 20px' }}>
              <div style={{ fontWeight: 700, color: form.textPrimary, marginBottom: '5px', fontSize: '0.95rem' }}>Exemple de carte</div>
              <div style={{ color: form.textSecondary, fontSize: '0.82rem', marginBottom: '12px' }}>Voici comment apparaîtront les panneaux dans l'interface.</div>
              <button style={{ backgroundColor: form.btnPrimaryBg, color: form.btnPrimaryText, border: 'none', borderRadius: form.btnRadius + 'px', padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>Action</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── CARTES MEMBRES ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1e40af" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
          <div>
            <div style={s.sTitle}>Cartes membres</div>
            <div style={s.sDesc}>Nom de l'église, logo et couleurs des cartes par statut</div>
          </div>
        </div>
        <div style={s.sBody}>
          {/* Nom + logo */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={s.label}>Nom de l'église (sur les cartes)</label>
              <input style={s.input} value={church.church_name} onChange={e => updChurch('church_name', e.target.value)} placeholder="RCC" maxLength={60} />
            </div>
            <div>
              <label style={s.label}>Logo de l'église</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '8px', border: '2px dashed #d1d5db', backgroundColor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  }
                </div>
                <input ref={logoFileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleLogoFile} />
                <button style={{ backgroundColor: '#f1f5f9', color: '#374151', border: '1px solid #d1d5db', borderRadius: '7px', padding: '7px 12px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
                  onClick={() => logoFileRef.current?.click()}>
                  Choisir image
                </button>
                {logoPreview && (
                  <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '7px', padding: '7px 10px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                    onClick={handleRemoveLogo}>
                    Supprimer
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Couleurs par statut */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151', marginBottom: '12px' }}>
              Couleurs des cartes par statut
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
              {Object.entries(STATUS_LABELS_CARD).map(([status, label]) => {
                const c = (church.colors || {})[status] || { from: '#1e40af', to: '#1e3a8a' };
                return (
                  <div key={status} style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '12px', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.8rem', color: '#374151', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ width: '14px', height: '14px', borderRadius: '3px', background: `linear-gradient(135deg, ${c.from}, ${c.to})` }} />
                      {label}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>Couleur début</div>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input type="color" value={c.from} onChange={e => updColor(status, 'from', e.target.value)}
                            style={{ width: '30px', height: '30px', border: 'none', cursor: 'pointer', borderRadius: '4px', padding: 0 }} />
                          <input style={{ ...s.input, maxWidth: '80px', fontFamily: 'monospace', fontSize: '0.75rem', padding: '4px 6px' }}
                            value={c.from} onChange={e => updColor(status, 'from', e.target.value)} maxLength={7} />
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>Couleur fin</div>
                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                          <input type="color" value={c.to} onChange={e => updColor(status, 'to', e.target.value)}
                            style={{ width: '30px', height: '30px', border: 'none', cursor: 'pointer', borderRadius: '4px', padding: 0 }} />
                          <input style={{ ...s.input, maxWidth: '80px', fontFamily: 'monospace', fontSize: '0.75rem', padding: '4px 6px' }}
                            value={c.to} onChange={e => updColor(status, 'to', e.target.value)} maxLength={7} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Filigrane */}
          <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '4px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Filigrane (watermark)
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '12px', alignItems: 'end' }}>
              <div>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '5px' }}>
                  Texte du filigrane <span style={{ color: '#94a3b8', fontWeight: 400 }}>(laissez vide pour utiliser le logo)</span>
                </label>
                <input
                  value={watermarkText}
                  onChange={e => setWatermarkText(e.target.value)}
                  placeholder={`Ex: ${church.church_name || 'RCC'}`}
                  style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '7px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div style={{ minWidth: '120px' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '5px' }}>
                  Opacité : <strong style={{ color: '#1e293b' }}>{Math.round(watermarkOpacity * 100)}%</strong>
                </label>
                <input
                  type="range" min="1" max="50" step="1"
                  value={Math.round(watermarkOpacity * 100)}
                  onChange={e => setWatermarkOpacity(Number(e.target.value) / 100)}
                  style={{ width: '100%', accentColor: '#6366f1' }}
                />
              </div>
            </div>
          </div>

          {/* Textes personnalisables de la carte */}
          <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '4px' }}>
            <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
              Textes de la carte membre
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '5px' }}>
                Texte du bas de carte <span style={{ color: '#94a3b8', fontWeight: 400 }}>(bande colorée)</span>
              </label>
              <input
                value={cardBottomText}
                onChange={e => setCardBottomText(e.target.value)}
                placeholder="Scannez le QR pour marquer votre présence"
                style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '7px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', display: 'block', marginBottom: '5px' }}>
                Légende sous la photo <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optionnel — ex: "Membre actif")</span>
              </label>
              <input
                value={cardPhotoCaption}
                onChange={e => setCardPhotoCaption(e.target.value)}
                placeholder="Ex: Église CVA · Abidjan"
                style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: '7px', fontSize: '0.85rem', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Mot de passe obligatoire */}
          <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151' }}>Mot de passe obligatoire à la création</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                    {requirePassword
                      ? 'Les nouveaux comptes doivent avoir un mot de passe'
                      : 'Les nouveaux comptes peuvent être créés sans mot de passe'}
                  </div>
                </div>
              </div>
              <div
                onClick={() => setRequirePassword(v => !v)}
                style={{
                  width: '44px', height: '24px', borderRadius: '999px', cursor: 'pointer', flexShrink: 0,
                  backgroundColor: requirePassword ? '#f59e0b' : '#d1d5db',
                  position: 'relative', transition: 'background-color 0.2s',
                }}>
                <div style={{
                  position: 'absolute', top: '3px',
                  left: requirePassword ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%',
                  backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s',
                }}/>
              </div>
            </div>
          </div>

          {/* Utiliser les Familles */}
          <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '16px', border: '1px solid #e2e8f0', marginBottom: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#374151' }}>Utiliser les Familles</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                    {useFamilies
                      ? 'La famille est obligatoire à l\'inscription et apparaît sur la carte'
                      : 'Aucune gestion des familles pour cette église'}
                  </div>
                </div>
              </div>
              <div
                onClick={() => setUseFamilies(v => !v)}
                style={{
                  width: '44px', height: '24px', borderRadius: '999px', cursor: 'pointer', flexShrink: 0,
                  backgroundColor: useFamilies ? '#7c3aed' : '#d1d5db',
                  position: 'relative', transition: 'background-color 0.2s',
                }}>
                <div style={{
                  position: 'absolute', top: '3px',
                  left: useFamilies ? '23px' : '3px',
                  width: '18px', height: '18px', borderRadius: '50%',
                  backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  transition: 'left 0.2s',
                }}/>
              </div>
            </div>
          </div>

          {useFamilies && (
            <div style={{ backgroundColor: '#fafafa', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e2e8f0', marginBottom: '4px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', color: '#374151' }}>Gérer les familles (créer, modifier, consulter les membres)</span>
              <a href="/familles" style={{ background: '#7c3aed', color: '#fff', borderRadius: '7px', padding: '8px 16px', fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none' }}>
                Voir les familles →
              </a>
            </div>
          )}

          {/* Preview carte */}
          <div style={{ backgroundColor: '#f0f4ff', borderRadius: '10px', padding: '16px', border: '1px solid #c7d2fe' }}>
            <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#374151', marginBottom: '10px' }}>
              Aperçu de la carte — sélectionner un statut :
            </div>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', flexWrap: 'wrap' }}>
              {Object.entries(STATUS_LABELS_CARD).map(([status, label]) => (
                <button key={status} onClick={() => setPreviewStatus(status)}
                  style={{ padding: '5px 14px', borderRadius: '999px', border: '1.5px solid', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600,
                    borderColor: previewStatus === status ? '#1e40af' : '#d1d5db',
                    backgroundColor: previewStatus === status ? '#eff6ff' : 'white',
                    color: previewStatus === status ? '#1e40af' : '#64748b' }}>
                  {label}
                </button>
              ))}
            </div>
            <CardPreview
              churchName={church.church_name}
              logoPreview={logoPreview}
              colors={church.colors || {}}
              status={previewStatus}
              watermarkText={watermarkText}
              watermarkOpacity={watermarkOpacity}
              cardBottomText={cardBottomText}
              cardPhotoCaption={cardPhotoCaption}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', opacity: churchSaving ? 0.7 : 1 }}
              onClick={handleSaveChurch} disabled={churchSaving}>
              {churchSaving ? 'Sauvegarde...' : 'Enregistrer les cartes'}
            </button>
          </div>
        </div>
      </div>

      {/* ── AFFICHES & ÉVÉNEMENTS (landing page de cette église) ── */}
      {(activeChurchId || !canSwitchChurch) && <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
          <div>
            <div style={s.sTitle}>Affiches & Événements</div>
            <div style={s.sDesc}>{news.length} affiche{news.length > 1 ? 's' : ''} — image de l'événement + lien YouTube, Facebook, TikTok... affichées sur votre page publique</div>
          </div>
        </div>
        <div style={s.sBody}>
          {!canEditNews ? (
            <div style={{ padding: '18px 16px', background: '#fef2f2', borderRadius: 10, border: '1px solid #fecaca', fontSize: '0.85rem', color: '#991b1b', display: 'flex', alignItems: 'center', gap: 10 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              <div>Accès non accordé par l'administrateur. Contactez votre administrateur pour obtenir le droit de modifier les affiches.</div>
            </div>
          ) : <>
          <div style={{ marginBottom: 14, padding: '10px 14px', background: '#f0f4ff', borderRadius: 8, border: '1px solid #c7d2fe', fontSize: '0.78rem', color: '#4338ca', display: 'flex', alignItems: 'center', gap: 8 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            Ajoutez une URL d'image (affiche) et un lien vers YouTube, Facebook, TikTok etc. Les visiteurs cliqueront pour aller sur la vidéo ou la page.
          </div>

          {news.map((item, i) => (
            <AfficheCard key={i} item={item} index={i}
              onChange={(updates) => setNews(prev => prev.map((x,j) => j===i ? {...x,...updates} : x))}
              onDelete={() => setNews(prev => prev.filter((_,j) => j !== i))}
              s={s}
            />
          ))}

          <button
            onClick={() => setNews(prev => [...prev, { title: '', desc: '', image_url: '', link_url: '', link_type: 'link', badge: '' }])}
            style={{ width: '100%', padding: '11px', border: '2px dashed #d1d5db', borderRadius: 12, background: 'transparent', cursor: 'pointer', fontSize: '0.88rem', color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#7c3aed'; e.currentTarget.style.color = '#7c3aed'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.color = '#64748b'; }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Ajouter une actualité
          </button>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
            <button
              style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', opacity: newsSaving ? 0.7 : 1 }}
              onClick={handleSaveNews} disabled={newsSaving}>
              {newsSaving ? 'Sauvegarde...' : 'Enregistrer les affiches'}
            </button>
          </div>
          </>}
        </div>
      </div>}

      {/* ── APERÇU NAVBAR COMPLÈTE ── */}
      <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><rect x="3" y="3" width="18" height="4" rx="1"/><rect x="3" y="10" width="5" height="11" rx="1"/><rect x="10" y="10" width="11" height="5" rx="1"/><rect x="10" y="17" width="5" height="4" rx="1"/><rect x="17" y="17" width="4" height="4" rx="1"/></svg>
          <div><div style={s.sTitle}>Aperçu global</div><div style={s.sDesc}>Rendu final de l'interface</div></div>
        </div>
        <div style={s.sBody}>
          <div style={{ borderRadius: '10px', overflow: 'hidden', border: `1px solid ${form.borderColor}` }}>
            {/* Navbar */}
            <div style={{ backgroundColor: form.primaryColor, padding: '11px 18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '5px', padding: '4px 7px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </div>
              {form.logoUrl
                ? <img src={form.logoUrl} alt="logo" style={{ width: '26px', height: '26px', borderRadius: '5px', objectFit: 'cover' }} />
                : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.6"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              }
              <span style={{ fontWeight: 700, color: 'white', fontSize: '1rem', fontFamily: form.fontFamily }}>{form.siteName}</span>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem', color: 'white', fontWeight: 600 }}>ifofana</span>
                <div style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', borderRadius: form.btnRadius + 'px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600 }}>Déconnexion</div>
              </div>
            </div>
            {/* Layout */}
            <div style={{ display: 'flex', height: '160px' }}>
              {/* Sidebar */}
              <div style={{ width: '160px', backgroundColor: form.sidebarBg, padding: '10px 0', flexShrink: 0 }}>
                {['Tableau de bord','Activités','Présences','Participants'].map((item, i) => (
                  <div key={item} style={{ padding: '7px 14px', fontSize: '0.78rem', color: i === 0 ? 'white' : form.sidebarText, backgroundColor: i === 0 ? form.sidebarActive : 'transparent', borderRadius: '5px', margin: '2px 6px', fontFamily: form.fontFamily }}>
                    {item}
                  </div>
                ))}
              </div>
              {/* Content */}
              <div style={{ flex: 1, backgroundColor: form.pageBg, padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ backgroundColor: form.cardBg, borderRadius: form.cardRadius + 'px', border: `1px solid ${form.borderColor}`, boxShadow: form.cardShadow, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: form.textPrimary, fontFamily: form.fontFamily }}>Tableau de bord</div>
                    <div style={{ fontSize: '0.7rem', color: form.textSecondary, fontFamily: form.fontFamily }}>Bienvenue sur l'interface</div>
                  </div>
                  <button style={{ backgroundColor: form.btnPrimaryBg, color: form.btnPrimaryText, border: 'none', borderRadius: form.btnRadius + 'px', padding: '5px 12px', fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', fontFamily: form.fontFamily }}>Action</button>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {['Activités','Présences','Membres'].map(t => (
                    <div key={t} style={{ flex: 1, backgroundColor: form.cardBg, borderRadius: form.cardRadius + 'px', border: `1px solid ${form.borderColor}`, boxShadow: form.cardShadow, padding: '8px', textAlign: 'center' }}>
                      <div style={{ fontSize: '1rem', fontWeight: 800, color: form.primaryColor, fontFamily: form.fontFamily }}>12</div>
                      <div style={{ fontSize: '0.62rem', color: form.textSecondary, fontFamily: form.fontFamily }}>{t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── CONFIGURATION EMAIL (superadmin uniquement) ── */}
      {isPlatformAdmin && <div style={s.section}>
        <div style={s.sHead}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
          <div>
            <div style={s.sTitle}>Configuration Email (SMTP)</div>
            <div style={s.sDesc}>Paramètres d'envoi d'emails — notifications d'adhésion, réinitialisation de mot de passe</div>
          </div>
        </div>
        <div style={s.sBody}>

          {/* Toggle activer */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', backgroundColor: emailCfg.enabled ? '#f0fdf4' : '#f8fafc', borderRadius: '10px', border: `1px solid ${emailCfg.enabled ? '#bbf7d0' : '#e2e8f0'}`, marginBottom: '18px', cursor: 'pointer' }}
            onClick={() => setEmailCfg(p => ({ ...p, enabled: !p.enabled }))}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: emailCfg.enabled ? '#22c55e' : '#94a3b8' }}/>
              <div>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Envoi d'emails {emailCfg.enabled ? 'activé' : 'désactivé'}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Les notifications (demandes d'adhésion, réinitialisation) utilisent cette configuration</div>
              </div>
            </div>
            <div style={{ width: '44px', height: '24px', borderRadius: '999px', backgroundColor: emailCfg.enabled ? '#22c55e' : '#d1d5db', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: '2px', left: emailCfg.enabled ? '22px' : '2px', width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }}/>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={s.label}>Serveur SMTP</label>
              <input style={s.input} value={emailCfg.smtp_host} onChange={e => setEmailCfg(p => ({ ...p, smtp_host: e.target.value }))} placeholder="smtp.gmail.com"/>
            </div>
            <div>
              <label style={s.label}>Port</label>
              <input type="number" style={s.input} value={emailCfg.smtp_port} onChange={e => setEmailCfg(p => ({ ...p, smtp_port: parseInt(e.target.value) || 587 }))} placeholder="587"/>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={s.label}>Identifiant SMTP (email)</label>
              <input style={s.input} value={emailCfg.smtp_username} onChange={e => setEmailCfg(p => ({ ...p, smtp_username: e.target.value }))} placeholder="votre@gmail.com"/>
            </div>
            <div>
              <label style={s.label}>
                Mot de passe SMTP
                {emailCfg.has_password && !emailCfg.smtp_password && (
                  <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#16a34a', fontWeight: 600 }}>
                    (configuré — laisser vide pour conserver)
                  </span>
                )}
                {!emailCfg.has_password && (
                  <span style={{ marginLeft: '8px', fontSize: '0.72rem', color: '#f59e0b', fontWeight: 600 }}>
                    (non configuré)
                  </span>
                )}
              </label>
              <div style={{ position: 'relative' }}>
                <input type={showSmtpPwd ? 'text' : 'password'} style={{ ...s.input, paddingRight: '40px', borderColor: !emailCfg.has_password && !emailCfg.smtp_password ? '#fbbf24' : undefined }}
                  value={emailCfg.smtp_password} onChange={e => setEmailCfg(p => ({ ...p, smtp_password: e.target.value }))}
                  placeholder={emailCfg.has_password ? 'Laisser vide pour conserver' : 'Entrez le mot de passe SMTP'}/>
                <button type="button" onClick={() => setShowSmtpPwd(v => !v)}
                  style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}>
                  {showSmtpPwd
                    ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              {emailCfg.smtp_password && (
                <div style={{ marginTop: '5px', fontSize: '0.73rem', color: '#16a34a', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  Mot de passe saisi — cliquez sur "Enregistrer" ou "Tester" pour l'appliquer
                </div>
              )}
              <div style={{ marginTop: '4px', fontSize: '0.71rem', color: '#94a3b8' }}>
                Le mot de passe est masqué après enregistrement pour des raisons de sécurité.
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '0.88rem', color: '#374151' }}>
              <input type="checkbox" checked={emailCfg.smtp_use_tls} onChange={e => setEmailCfg(p => ({ ...p, smtp_use_tls: e.target.checked, smtp_use_ssl: e.target.checked ? false : p.smtp_use_ssl }))} style={{ width: '16px', height: '16px' }}/>
              STARTTLS (port 587)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', fontSize: '0.88rem', color: '#374151' }}>
              <input type="checkbox" checked={emailCfg.smtp_use_ssl} onChange={e => setEmailCfg(p => ({ ...p, smtp_use_ssl: e.target.checked, smtp_use_tls: e.target.checked ? false : p.smtp_use_tls }))} style={{ width: '16px', height: '16px' }}/>
              SSL/TLS (port 465)
            </label>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
            <div>
              <label style={s.label}>Nom expéditeur</label>
              <input style={s.input} value={emailCfg.from_name} onChange={e => setEmailCfg(p => ({ ...p, from_name: e.target.value }))} placeholder="RCC"/>
            </div>
            <div>
              <label style={s.label}>Email expéditeur</label>
              <input type="email" style={s.input} value={emailCfg.from_email} onChange={e => setEmailCfg(p => ({ ...p, from_email: e.target.value }))} placeholder="noreply@eglise.com"/>
            </div>
          </div>

          <div style={{ marginBottom: '18px' }}>
            <label style={s.label}>
              Email administrateur
              <span style={{ fontWeight: 400, color: '#64748b' }}> — reçoit les notifications de demandes d'adhésion</span>
            </label>
            <input type="email" style={s.input} value={emailCfg.admin_email} onChange={e => setEmailCfg(p => ({ ...p, admin_email: e.target.value }))} placeholder="admin@eglise.com"/>
          </div>

          {/* Info Gmail */}
          <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '0.82rem', color: '#0369a1', lineHeight: 1.7 }}>
            <div style={{ fontWeight: 700, marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0369a1" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Comment générer un mot de passe d'application Gmail
            </div>
            <ol style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <li>Allez sur <strong>myaccount.google.com/security</strong></li>
              <li>Activez la <strong>validation en 2 étapes</strong> si ce n'est pas fait</li>
              <li>Cherchez <strong>"Mots de passe des applications"</strong> dans la barre de recherche du compte</li>
              <li>Créez un nouveau mot de passe pour l'app <strong>"Autre"</strong> → nommez-le "Église"</li>
              <li>Copiez les <strong>16 caractères</strong> générés et collez-les dans le champ ci-dessus</li>
            </ol>
            <div style={{ marginTop: '8px', padding: '8px 10px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #bae6fd', fontFamily: 'monospace', fontSize: '0.78rem' }}>
              Host: smtp.gmail.com &nbsp;|&nbsp; Port: 587 &nbsp;|&nbsp; TLS: activé &nbsp;|&nbsp; SSL: désactivé
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button style={{ backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', opacity: emailSaving ? 0.7 : 1 }}
              onClick={handleSaveEmail} disabled={emailSaving}>
              {emailSaving ? 'Sauvegarde...' : 'Enregistrer la config email'}
            </button>
            <button style={{ backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.88rem', opacity: emailTesting ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '6px' }}
              onClick={handleTestEmail} disabled={emailTesting || !emailCfg.enabled}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/></svg>
              {emailTesting ? 'Envoi...' : 'Envoyer un email test'}
            </button>
          </div>
          {!emailCfg.enabled && <p style={{ fontSize: '0.77rem', color: '#94a3b8', marginTop: '6px' }}>Activez l'envoi d'emails pour pouvoir tester.</p>}
        </div>
      </div>}

      {/* Actions finales */}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px', marginBottom: '32px' }}>
        <button style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px 18px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' }}
          onClick={handleReset}>
          Réinitialiser tout
        </button>
        <button style={{ backgroundColor: form.primaryColor, color: 'white', border: 'none', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem' }}
          onClick={handleSave}>
          Enregistrer les modifications
        </button>
      </div>
    </div>
  );
}
